import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import EmptyState from "../components/app/EmptyState";
import Avatar from "../components/app/Avatar";
import ActivityFeed from "../components/app/ActivityFeed";
import AuthImage from "../components/app/AuthImage";
import {
  Sparkles, Calendar as CalIcon, Heart, Users, Search, MapPin, Clock,
  ChevronRight, ArrowRight, Plus, CreditCard, Trophy, MessageSquare, Zap,
} from "lucide-react";

/**
 * ParentDashboard — focused, minimal experience for the Parent role.
 *
 *  Sections (in order):
 *    1. Hero with the parent's name + clear next-action hints.
 *    2. Onboarding panel (only if no child linked AND no bookings yet).
 *    3. Upcoming sessions (real bookings — pulled from /api/bookings).
 *    4. Favorite coaches/academies preview.
 *    5. My children mini-grid (only if at least one child exists).
 *    6. Recent activity sidebar.
 *
 *  No "Players management", no "Upcoming Matches", no coach-only stat tiles.
 */
export default function ParentDashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [children, setChildren] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState({ coaches: [], academies: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, b, fav] = await Promise.all([
          api.get("/children").catch(() => ({ data: [] })),
          api.get("/bookings").catch(() => ({ data: [] })),
          api.get("/favorites").catch(() => ({ data: [] })),
        ]);
        setChildren(c.data || []);
        setBookings(b.data || []);
        // /api/favorites returns a flat list of coach docs — split by role
        const favList = Array.isArray(fav.data) ? fav.data : [];
        setFavorites({
          coaches: favList.filter((u) => u.role !== "academy"),
          academies: favList.filter((u) => u.role === "academy"),
        });
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  // Upcoming bookings: future slot_at AND not cancelled/rejected
  const upcomingBookings = useMemo(() => {
    const now = Date.now();
    return bookings
      .filter((b) => b.slot_at && new Date(b.slot_at).getTime() > now)
      .filter((b) => !["cancelled", "rejected"].includes(b.status))
      .sort((a, b) => new Date(a.slot_at) - new Date(b.slot_at))
      .slice(0, 5);
  }, [bookings]);

  const hasChildren = children.length > 0;
  const hasBookings = bookings.length > 0;
  const showOnboarding = !loading && !hasChildren && !hasBookings;

  const dateFmt = (iso) =>
    new Date(iso).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });

  const statusChip = (status) => {
    const map = {
      pending_payment: { label_bg: "Чака плащане", label_en: "Awaiting payment", cls: "amber" },
      pending_cash: { label_bg: "Кеш", label_en: "Cash pending", cls: "amber" },
      paid: { label_bg: "Платено", label_en: "Paid", cls: "" },
      confirmed: { label_bg: "Потвърдено", label_en: "Confirmed", cls: "" },
      completed: { label_bg: "Завършено", label_en: "Completed", cls: "platinum" },
    };
    const m = map[status] || map.pending_payment;
    return <span className={`chip-badge ${m.cls}`}>{lang === "bg" ? m.label_bg : m.label_en}</span>;
  };

  return (
    <Layout>
      <div className="space-y-6 otp-page-enter" data-testid="parent-dashboard">
        {/* Hero */}
        <div className="card-sharp p-6 sm:p-8 relative overflow-hidden rounded-lg" data-testid="parent-hero">
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ background: "radial-gradient(ellipse at top right, #3B82F6, transparent 60%)" }} />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="label-mono text-pitch mb-1">// {lang === "bg" ? "Родителски център" : "Parent center"}</div>
              <h1 className="font-oswald uppercase text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
                {lang === "bg" ? "Здравей" : "Hi"},<br />
                <span className="text-pitch">{user?.name?.split(" ")[0]}</span>
              </h1>
              {!showOnboarding && (
                <p className="text-white/55 mt-3 max-w-md text-sm">
                  {lang === "bg"
                    ? "Виж предстоящите си сесии и любимите си треньори."
                    : "Track your upcoming sessions and saved coaches."}
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to="/find-coaches" className="btn-primary px-4 py-2.5 inline-flex items-center gap-2 text-sm" data-testid="parent-discover-btn">
                <Search className="w-4 h-4" /> {lang === "bg" ? "Открий треньор" : "Find a coach"}
              </Link>
              {hasBookings && (
                <Link to="/bookings" className="btn-secondary px-4 py-2.5 inline-flex items-center gap-2 text-sm" data-testid="parent-bookings-btn">
                  <CreditCard className="w-4 h-4" /> {lang === "bg" ? "Моите резервации" : "My bookings"}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Onboarding empty state — only when zero bookings yet */}
        {showOnboarding && (
          <div className="card-sharp p-8 sm:p-12 text-center rounded-lg" data-testid="parent-onboarding">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-pitch" />
            </div>
            <h2 className="font-oswald uppercase text-2xl sm:text-3xl tracking-tight mb-3">
              {lang === "bg" ? "Добре дошъл в ONTHEPITCH" : "Welcome to ONTHEPITCH"}
            </h2>
            <p className="text-sm text-white/60 max-w-md mx-auto mb-6 leading-relaxed">
              {lang === "bg"
                ? "Открий треньор или академия, последвай любимите си и резервирай първата си сесия."
                : "Discover a coach or academy, follow your favorites, and book your first session."}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
              <Link to="/find-coaches" className="border border-pitch/40 hover:border-pitch hover:bg-pitch/[0.06] p-5 rounded-md transition-all group" data-testid="onboarding-find-coach">
                <div className="w-9 h-9 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center mb-3 mx-auto group-hover:bg-pitch group-hover:text-black transition-colors">
                  <Search className="w-4 h-4 text-pitch group-hover:text-black" />
                </div>
                <div className="font-oswald uppercase text-base tracking-tight mb-1">
                  {lang === "bg" ? "Открий треньор" : "Find a coach"}
                </div>
                <div className="text-[11px] text-white/50">
                  {lang === "bg" ? "Разгледай профили и резервирай първа сесия" : "Browse profiles and book your first session"}
                </div>
              </Link>
              <Link to="/coaches-map" className="border border-white/15 hover:border-pitch/55 hover:bg-pitch/[0.04] p-5 rounded-md transition-all group" data-testid="onboarding-map">
                <div className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/15 flex items-center justify-center mb-3 mx-auto group-hover:bg-pitch group-hover:text-black transition-colors">
                  <MapPin className="w-4 h-4 text-white/70 group-hover:text-black" />
                </div>
                <div className="font-oswald uppercase text-base tracking-tight mb-1">
                  {lang === "bg" ? "Карта на треньори" : "Coaches map"}
                </div>
                <div className="text-[11px] text-white/50">
                  {lang === "bg" ? "Виж кои треньори са близо до теб" : "See who's training near you"}
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Main grid */}
        {!showOnboarding && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              {/* Upcoming sessions */}
              <div className="card-sharp p-5 sm:p-6 rounded-lg" data-testid="parent-upcoming-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-mono">// {lang === "bg" ? "Предстоящи сесии" : "Upcoming sessions"}</div>
                    <h2 className="font-oswald uppercase text-xl sm:text-2xl tracking-tight">
                      {lang === "bg" ? "Следващи тренировки" : "Your next trainings"}
                    </h2>
                  </div>
                  {upcomingBookings.length > 0 && (
                    <Link to="/bookings" className="text-xs font-mono text-white/45 hover:text-pitch transition-colors inline-flex items-center gap-1">
                      {lang === "bg" ? "Всички" : "All"} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
                {upcomingBookings.length === 0 ? (
                  <EmptyState
                    icon={CalIcon}
                    title={lang === "bg" ? "Няма предстоящи" : "No upcoming sessions"}
                    description={lang === "bg" ? "Резервирай първата си сесия с треньор от платформата." : "Book your first session with a coach from the platform."}
                    cta={lang === "bg" ? "Открий треньор" : "Find a coach"}
                    ctaTo="/find-coaches"
                    compact
                    testId="empty-upcoming-bookings"
                  />
                ) : (
                  <div className="space-y-2">
                    {upcomingBookings.map((b) => (
                      <Link
                        to="/bookings"
                        key={b.id}
                        className="block border border-white/8 hover:border-pitch/40 rounded-md p-3 sm:p-4 transition-colors group"
                        data-testid={`upcoming-${b.id}`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className="w-10 h-10 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center flex-shrink-0">
                            <Trophy className="w-4 h-4 text-pitch" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-oswald uppercase tracking-tight text-sm sm:text-base truncate">
                              {dateFmt(b.slot_at)}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-white/55 font-mono mt-0.5 flex-wrap">
                              <span>{b.duration_min || 60} {lang === "bg" ? "мин" : "min"}</span>
                              <span className="text-white/20">·</span>
                              <span>€{b.amount || 0}</span>
                              {b.payment_method === "cash" && <><span className="text-white/20">·</span><span>💵 {lang === "bg" ? "Кеш" : "Cash"}</span></>}
                              {b.auto_confirm && <><span className="text-white/20">·</span><span className="text-gold inline-flex items-center gap-0.5" style={{ color: "#FFC857" }}><Zap className="w-3 h-3" /> Instant</span></>}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {statusChip(b.status)}
                            <ChevronRight className="w-4 h-4 text-white/25 group-hover:text-pitch transition-colors" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Favorites */}
              <div className="card-sharp p-5 rounded-lg" data-testid="parent-favorites-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="label-mono">// {lang === "bg" ? "Любими" : "Favorites"}</div>
                  <Link to="/favorites" className="text-xs font-mono text-white/45 hover:text-pitch transition-colors inline-flex items-center gap-1">
                    {lang === "bg" ? "Всички" : "All"} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <h3 className="font-oswald uppercase text-lg tracking-tight mb-3">
                  {lang === "bg" ? "Запазени треньори" : "Saved coaches"}
                </h3>
                {favorites.coaches.length === 0 && favorites.academies.length === 0 ? (
                  <div className="text-center py-6 border border-dashed border-white/10 rounded-md">
                    <Heart className="w-5 h-5 mx-auto mb-2 text-white/20" />
                    <div className="text-xs text-white/45">{lang === "bg" ? "Все още нямаш любими" : "No favorites yet"}</div>
                    <Link to="/find-coaches" className="text-[11px] text-pitch hover:underline mt-1.5 inline-block" data-testid="favorites-empty-cta">
                      {lang === "bg" ? "Разгледай" : "Browse"}
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {favorites.coaches.slice(0, 4).map((c) => (
                      <Link
                        to={`/coach/${c.id}`}
                        key={c.id}
                        className="flex items-center gap-2.5 p-2 -mx-2 hover:bg-white/[0.03] rounded-md transition-colors group"
                        data-testid={`fav-coach-${c.id}`}
                      >
                        <Avatar name={c.name} src={c.photo_url} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="font-oswald uppercase tracking-tight text-sm truncate">{c.name}</div>
                          {c.city && <div className="text-[10px] text-white/40 flex items-center gap-1"><MapPin className="w-2.5 h-2.5 text-pitch" />{c.city}</div>}
                        </div>
                        <Heart className="w-3.5 h-3.5 text-pitch fill-pitch flex-shrink-0" />
                      </Link>
                    ))}
                    {favorites.academies.slice(0, 2).map((a) => (
                      <Link
                        to={`/academy/${a.id}`}
                        key={a.id}
                        className="flex items-center gap-2.5 p-2 -mx-2 hover:bg-white/[0.03] rounded-md transition-colors group"
                        data-testid={`fav-acad-${a.id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center flex-shrink-0">
                          <Users className="w-3.5 h-3.5 text-pitch" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-oswald uppercase tracking-tight text-sm truncate">{a.academy_name || a.name}</div>
                          <div className="text-[10px] text-white/40">{lang === "bg" ? "Академия" : "Academy"}</div>
                        </div>
                        <Heart className="w-3.5 h-3.5 text-pitch fill-pitch flex-shrink-0" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick links */}
              <div className="card-sharp p-5 rounded-lg" data-testid="parent-quick-card">
                <div className="label-mono mb-3">// {lang === "bg" ? "Бързи действия" : "Quick links"}</div>
                <div className="space-y-1.5">
                  <QuickLink to="/coaches-map" icon={MapPin} label={lang === "bg" ? "Карта на треньори" : "Coaches map"} testId="quick-map" />
                  <QuickLink to="/chat" icon={MessageSquare} label={lang === "bg" ? "Чат" : "Chat"} testId="quick-chat" />
                  <QuickLink to="/find-coaches" icon={Search} label={lang === "bg" ? "Открий треньор" : "Discover"} testId="quick-discover" />
                </div>
              </div>

              {/* Recent activity */}
              <div className="card-sharp p-5 rounded-lg" data-testid="parent-activity-card">
                <div className="label-mono mb-3">// {lang === "bg" ? "Активност" : "Recent activity"}</div>
                <ActivityFeed limit={5} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function QuickLink({ to, icon: Icon, label, testId }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between p-2.5 -mx-1 hover:bg-white/[0.03] rounded-md transition-colors group"
      data-testid={testId}
    >
      <span className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-pitch" />
        <span className="font-oswald uppercase tracking-tight text-sm">{label}</span>
      </span>
      <ChevronRight className="w-3.5 h-3.5 text-white/25 group-hover:text-pitch transition-colors" />
    </Link>
  );
}
